import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PresentationBrandAssetSlot } from "@/api/admin/skills"

import { getPresentationBrandAssetContent } from "@/api/admin/skills"
import { ImageIcon, Trash2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type PresentationBrandLogoCardProps = {
  title: string
  description: string
  slotId: string
  slot: PresentationBrandAssetSlot | null
  required?: boolean
  uploading?: boolean
  onUpload: (file: File) => void
  onRemove?: () => void
}

export function PresentationBrandLogoCard({
  title,
  description,
  slotId,
  slot,
  required = false,
  uploading = false,
  onUpload,
  onRemove,
}: PresentationBrandLogoCardProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const version = slot?.blob?.version

  useEffect(() => {
    if (!slot?.blob) {
      setPreviewUrl(null)
      return
    }
    let objectUrl: string | null = null
    let cancelled = false
    void getPresentationBrandAssetContent(slotId)
      .then((blob) => {
        if (cancelled) {
          return
        }
        objectUrl = URL.createObjectURL(blob)
        setPreviewUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewUrl(null)
        }
      })
    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [slotId, version, slot?.blob])

  const takeFile = (file: File | undefined) => {
    if (file) {
      onUpload(file)
    }
  }

  let dropHint = t("adminDashboard.systemConfig.presentationBrand.dropLogo", {
    defaultValue: "Drop a PNG, JPEG, or WebP — or click to choose",
  })
  if (uploading) {
    dropHint = t("adminDashboard.systemConfig.presentationBrand.uploading", {
      defaultValue: "Uploading…",
    })
  } else if (slot?.blob) {
    dropHint = t("adminDashboard.systemConfig.presentationBrand.replaceLogo", {
      defaultValue: "Click or drop to replace",
    })
  }

  return (
    <div className="border-border space-y-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-muted-foreground text-xs">{description}</p>
        </div>
        <Badge variant="outline">
          {required
            ? t("adminDashboard.systemConfig.presentationBrand.required", {
                defaultValue: "Required",
              })
            : t("adminDashboard.systemConfig.presentationBrand.optional", {
                defaultValue: "Optional",
              })}
        </Badge>
      </div>

      <button
        type="button"
        className={`border-border bg-muted/30 flex min-h-24 w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed p-3 text-center ${
          dragOver ? "border-primary bg-accent/40" : ""
        }`}
        onClick={() => inputRef.current?.click()}
        onDragLeave={() => setDragOver(false)}
        onDragOver={(event) => {
          event.preventDefault()
          setDragOver(true)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setDragOver(false)
          takeFile(event.dataTransfer.files[0])
        }}
      >
        {previewUrl ? (
          <img alt="" className="h-14 w-auto max-w-full object-contain" src={previewUrl} />
        ) : (
          <div className="bg-muted flex size-10 items-center justify-center rounded-lg">
            <ImageIcon className="text-muted-foreground size-5" />
          </div>
        )}
        <span className="text-muted-foreground text-xs">{dropHint}</span>
      </button>

      <input
        ref={inputRef}
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        type="file"
        onChange={(event) => {
          takeFile(event.target.files?.[0])
          event.target.value = ""
        }}
      />

      {slot ? (
        <p className="text-muted-foreground text-xs">
          {slot.filename}
          {slot.blob?.width && slot.blob.height
            ? ` · ${slot.blob.width}×${slot.blob.height}px`
            : ""}
        </p>
      ) : null}

      {slot && onRemove ? (
        <Button disabled={uploading} size="sm" type="button" variant="ghost" onClick={onRemove}>
          <Trash2 className="size-4" />
          {t("adminDashboard.systemConfig.presentationBrand.remove", { defaultValue: "Remove" })}
        </Button>
      ) : null}
    </div>
  )
}
